import { Button } from "@/components/ui/button";
import { Trash2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { useState } from "react";
import { format } from "date-fns";

function formatTime(timeStr) {
  if (!timeStr) return "";
  const parts = timeStr.split(":");
  if (parts.length < 2) return timeStr;
  const h = parseInt(parts[0], 10);
  const minute = parts[1];
  const ampm = h >= 12 ? "PM" : "AM";
  const formattedHour = ((h + 11) % 12) + 1;
  return `${formattedHour.toString().padStart(2, "0")}:${minute} ${ampm}`;
}

export function DeleteConfirmDialog({
  isOpen,
  onClose,

  onConfirm,
  title = "Confirm Delete",
  description = "You want to delete all the marked item, this can’t be undone once you delete.",
  isLoading = false,
}) {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader className={`flex justify-center items-center gap-2`}>
          <Button className="bg-cblack7" size="icon" variant="">
            <Trash2 className="text-cred" />
          </Button>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <div className={`flex w-full justify-center gap-5 items-center`}>
            <Button className="bg-cblack7 text-white" onClick={onClose}>
              Cancel
            </Button>
            <Button
              isLoading={isLoading}
              variant="destructive"
              className="bg-cred"
              onClick={() => {
                onConfirm();
                onClose();
              }}
            >
              Yes, Delete
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ViewShiftDetails({ isOpen, onClose, shifts = [], shiftId }) {
  const daysOfWeek = [
    "saturday",
    "sunday",
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
  ];

  const formatTimeRange = (start, end) => {
    return `${format(new Date(start), "hh:mm a")} - ${format(
      new Date(end),
      "hh:mm a"
    )}`;
  };

  const shiftsByDay = daysOfWeek?.map((day) => {
    const dayShifts =
      shifts?.length > 0 &&
      shifts
        ?.filter((shift) => shift?.day?.toLowerCase() === day)
        .flatMap((shift) => shift.time_slots || []);

    return { day, shifts: dayShifts };
  });

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle>Shift Details: {shiftId}</DialogTitle>
        </DialogHeader>
        <Separator />
        <div className="py-5 flex flex-col">
          <div className="bg-cblack font-medium px-4 w-full flex py-2">
            <h2 className="w-32">Date</h2>
            <p>Time</p>
          </div>

          {shiftsByDay?.map(({ day, shifts }) => (
            <div
              key={day}
              className="mt-2 px-5 text-cwhite2 flex py-3 border-b border-neutral-700"
            >
              <p className="w-32 capitalize">{day}</p>
              <div className="flex gap-2 flex-wrap">
                {shifts.length > 0 ? (
                  shifts?.map((shiftSlot, index) => (
                    <span
                      key={index}
                      className="p-2 border border-neutral-600 hover:bg-cblack7 bg-cblack6 rounded text-sm cursor-pointer"
                    >
                      {formatTimeRange(
                        shiftSlot.start_time,
                        shiftSlot.end_time
                      )}
                    </span>
                  ))
                ) : (
                  <span className="text-sm text-cwhite3">No shift</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function SelectShiftDialog({
  isOpen,
  onClose,
  shifts = [],
  setShiftValue,
  selectedShift = [],
  setSelectedShift,
}) {
  const handleShiftClick = (id) => {
    const numId = Number(id);
    setSelectedShift((prev) => {
      const arr = (prev || []).map(Number);
      return arr.includes(numId)
        ? arr.filter((sid) => sid !== numId)
        : [...arr, numId];
    });
  };

  const isSelected = (id) => {
    const numId = Number(id);
    return (selectedShift || []).some((sid) => Number(sid) === numId);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-4xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Select Shift</DialogTitle>
        </DialogHeader>
        <Separator />

        <div className="py-5 flex flex-col">
          {/* Header */}
          <div className="bg-cblack font-medium px-4 w-full flex py-2 text-sm text-neutral-300 rounded">
            <h2 className="w-32">Date</h2>
            <p>Time</p>
          </div>

          {/* Dynamic Days and Shifts */}
          <div className="divide-y divide-neutral-800">
            {shifts && shifts.length > 0 ? (
              shifts.map((dayShift) => (
                <div
                  key={dayShift.id || dayShift.day}
                  className="px-5 text-cwhite2 flex flex-col sm:flex-row sm:items-center py-3 border-b border-neutral-700 gap-2"
                >
                  <p className="w-32 capitalize font-medium text-white shrink-0">
                    {dayShift.day}
                  </p>
                  <div className="flex gap-2 flex-wrap items-center">
                    {dayShift.shifts && dayShift.shifts.length > 0 ? (
                      dayShift.shifts.map((shift) => {
                        const start = formatTime(shift.start_time);
                        const end = formatTime(shift.end_time);
                        const selected = isSelected(shift.id);

                        return (
                          <button
                            type="button"
                            key={shift.id}
                            onClick={() => handleShiftClick(shift.id)}
                            className={`p-2 border rounded text-sm cursor-pointer transition-all duration-150 flex items-center gap-1.5 ${
                              selected
                                ? "!bg-cyellow !text-black !border-cyellow shadow-sm font-semibold"
                                : "bg-cblack6 text-cwhite2 border-neutral-600 hover:bg-cblack7"
                            }`}
                          >
                            <span>
                              {start} - {end}
                            </span>
                          </button>
                        );
                      })
                    ) : (
                      <span className="text-xs text-neutral-500 italic">
                        No shift
                      </span>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-6 text-neutral-400 text-sm">
                No shift schedules found
              </div>
            )}
          </div>

          {/* Confirm Button */}
          <div className="mt-5 flex gap-3">
            <Button
              type="button"
              onClick={() => {
                const finalShifts = (selectedShift || []).map(Number);
                if (setShiftValue) {
                  setShiftValue(finalShifts);
                }
                onClose();
              }}
              className="bg-cgreen hover:bg-green-600 w-full text-cblack font-semibold"
            >
              Confirm
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function UpdateShiftDialog({ isOpen, onClose, details }) {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle>Select Shift</DialogTitle>
        </DialogHeader>
        <Separator />
        <div className="py-5 flex flex-col">
          <div className="bg-cblack font-medium px-4 w-full flex py-2">
            <h2 className="w-32 ">Date</h2>
            <p>Time</p>
          </div>
          <div className="mt-2 px-5 text-cwhite2 flex  py-3 border-b border-neutral-700">
            <p className="w-32 ">Saturday</p>
            <div className="flex gap-2">
              <span className="p-2 border border-neutral-600 hover:bg-cblack7 bg-cyellow text-black rounded text-sm cursor-pointer">
                08:00AM - 04:00PM
              </span>
              <span className="p-2 border border-neutral-600 hover:bg-cblack7 bg-cblack6 rounded text-sm cursor-pointer">
                04:00PM - 02:00AM
              </span>
              <span className="p-2 border border-neutral-600 hover:bg-cblack7 bg-cblack6 rounded text-sm cursor-pointer">
                02:00AM - 04:00AM
              </span>
            </div>
          </div>
          <div className="mt-2 px-5 text-cwhite2 flex  py-3 border-b border-neutral-700">
            <p className="w-32 ">Sunday</p>
            <div className="flex gap-2">
              <span className="p-2 border border-neutral-600 hover:bg-cblack7 bg-cyellow text-black rounded text-sm cursor-pointer">
                08:00AM - 04:00PM
              </span>
              <span className="p-2 border border-neutral-600 hover:bg-cblack7 bg-cyellow text-black rounded text-sm cursor-pointer">
                04:00PM - 02:00AM
              </span>
              <span className="p-2 border border-neutral-600 hover:bg-cblack7 bg-cblack6 rounded text-sm cursor-pointer">
                02:00AM - 04:00AM
              </span>
            </div>
          </div>
          <div className="mt-2 px-5 text-cwhite2 flex  py-3 border-b border-neutral-700">
            <p className="w-32 ">Monday</p>
            <div className="flex gap-2">
              <span className="p-2 border border-neutral-600 hover:bg-cblack7 bg-cblack6 rounded text-sm cursor-pointer">
                08:00AM - 04:00PM
              </span>
              <span className="p-2 border border-neutral-600 hover:bg-cblack7 bg-cblack6 rounded text-sm cursor-pointer">
                04:00PM - 02:00AM
              </span>
              <span className="p-2 border border-neutral-600 hover:bg-cblack7 bg-cblack6 rounded text-sm cursor-pointer">
                02:00AM - 04:00AM
              </span>
            </div>
          </div>
          <div className="mt-2 px-5 text-cwhite2 flex  py-3 border-b border-neutral-700">
            <p className="w-32 ">Tuesday</p>
            <div className="flex gap-2">
              <span className="p-2 border border-neutral-600 hover:bg-cblack7 bg-cblack6 rounded text-sm cursor-pointer">
                08:00AM - 04:00PM
              </span>
              <span className="p-2 border border-neutral-600 hover:bg-cblack7 bg-cyellow text-black rounded text-sm cursor-pointer">
                04:00PM - 02:00AM
              </span>
              <span className="p-2 border border-neutral-600 hover:bg-cblack7 bg-cblack6 rounded text-sm cursor-pointer">
                02:00AM - 04:00AM
              </span>
            </div>
          </div>
          <div className="mt-2 px-5 text-cwhite2 flex  py-3 border-b border-neutral-700">
            <p className="w-32 ">Wednesday</p>
            <div className="flex gap-2">
              <span className="p-2 border border-neutral-600 hover:bg-cblack7 bg-cblack6 rounded text-sm cursor-pointer">
                08:00AM - 04:00PM
              </span>
              <span className="p-2 border border-neutral-600 hover:bg-cblack7 bg-cblack6 rounded text-sm cursor-pointer">
                04:00PM - 02:00AM
              </span>
              <span className="p-2 border border-neutral-600 hover:bg-cblack7 bg-cyellow text-black rounded text-sm cursor-pointer">
                02:00AM - 04:00AM
              </span>
            </div>
          </div>
          <div className="mt-2 px-5 text-cwhite2 flex  py-3 border-b border-neutral-700">
            <p className="w-32 ">Thursday</p>
            <div className="flex gap-2">
              <span className="p-2 border border-neutral-600 hover:bg-cblack7 bg-cblack6 rounded text-sm cursor-pointer">
                08:00AM - 04:00PM
              </span>
              <span className="p-2 border border-neutral-600 hover:bg-cblack7 bg-cyellow text-black rounded text-sm cursor-pointer">
                04:00PM - 02:00AM
              </span>
              <span className="p-2 border border-neutral-600 hover:bg-cblack7 bg-cyellow text-black rounded text-sm cursor-pointer">
                02:00AM - 04:00AM
              </span>
            </div>
          </div>
          <div className="mt-2 px-5 text-cwhite2 flex  py-3">
            <p className="w-32 ">Friday</p>
            <div className="flex gap-2">
              <span className="p-2 border border-neutral-600 hover:bg-cblack7 bg-cblack6 rounded text-sm cursor-pointer">
                08:00AM - 04:00PM
              </span>
              <span className="p-2 border border-neutral-600 hover:bg-cblack7 bg-cyellow text-black rounded text-sm cursor-pointer">
                04:00PM - 02:00AM
              </span>
              <span className="p-2 border border-neutral-600 hover:bg-cblack7 bg-cyellow text-black rounded text-sm cursor-pointer">
                02:00AM - 04:00AM
              </span>
            </div>
          </div>
          <div className="mt-5">
            <Button className="bg-cgreen w-full text-cblack">Update</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
